<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col" style="height: calc(100vh - 220px); min-height: 560px;">
    <ComponentHeader
      title="Workflow Builder"
      subtitle="Compose custom data workflows on the canvas; submit for review and materialize on a cadence."
    >
      <!-- New Workflow lives in the composer (Clear button) + the empty-rail
           state, so the header only needs Refresh. -->
      <button
        class="btn-secondary"
        :disabled="isLoading"
        @click="refreshWorkflows"
      >
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="isLoading" />
        Refresh
      </button>
      <!-- Connect Workflow Builder to an external warehouse (Snowflake / Azure /
           JDBC-ODBC) so workflows can read+write the customer's own data. -->
      <button class="btn-accent ml-2" @click="connectionModalVisible = true">
        <font-awesome-icon :icon="['fas', 'database']" />
        Add Data Connection
      </button>
    </ComponentHeader>

    <!-- Split view: workflow list rail (left) + live canvas composer (right) -->
    <div ref="splitRow" class="flex flex-1 min-h-0">
      <!-- ── Left rail: workflows grouped by status ───────────────────────── -->
      <aside
        class="shrink-0 border-r border-gray-200 bg-gray-50/40 flex flex-col transition-all duration-200"
        :class="railCollapsed ? 'w-12' : 'w-72'"
      >
        <div class="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200">
          <button class="btn-icon" :title="railCollapsed ? 'Show workflows' : 'Hide workflows'" @click="railCollapsed = !railCollapsed">
            <font-awesome-icon :icon="['fas', railCollapsed ? 'chevron-right' : 'chevron-left']" />
          </button>
          <span v-if="!railCollapsed" class="text-xs font-semibold uppercase tracking-wide text-gray-500">Workflows</span>
          <span v-if="!railCollapsed" class="ml-auto text-xs text-gray-400 tabular-nums">{{ workflows.length }}</span>
        </div>

        <div v-if="!railCollapsed" class="flex flex-col min-h-0 flex-1">
          <!-- Search -->
          <div class="px-3 py-2 border-b border-gray-200">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name…"
              class="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
            />
          </div>

          <!-- Loading skeleton -->
          <div v-if="isLoading && !workflows.length" class="p-3 space-y-2">
            <div v-for="n in 5" :key="n" class="h-8 rounded skeleton-shimmer"></div>
          </div>

          <!-- Grouped list -->
          <div v-else class="overflow-y-auto flex-1 py-1">
            <template v-for="group in groupedWorkflows" :key="group.status">
              <div v-if="group.items.length" class="mb-1">
                <div class="flex items-center gap-2 px-3 py-1.5">
                  <span :class="statusDotClass(group.status)"></span>
                  <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{{ statusLabel(group.status) }}</span>
                  <span class="text-[11px] text-gray-400 tabular-nums">{{ group.items.length }}</span>
                </div>
                <!-- Row is a div, not a button: the overflow DropdownMenu renders
                     its own <button>, and nesting one button inside another is
                     invalid HTML (browsers reparent it, breaking both handlers).
                     The menu is a sibling of the clickable area so its clicks
                     never reach selectWorkflow. -->
                <div
                  v-for="wf in group.items"
                  :key="wf.id"
                  class="group w-full px-3 py-2 flex items-center gap-2 border-l-2 transition-colors"
                  :class="activeId === wf.id
                    ? 'bg-blue-50/70 border-blue-dark'
                    : 'border-transparent hover:bg-gray-100/70'"
                >
                  <div
                    class="text-left flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                    role="button"
                    tabindex="0"
                    @click="selectWorkflow(wf)"
                    @keydown.enter.prevent="selectWorkflow(wf)"
                    @keydown.space.prevent="selectWorkflow(wf)"
                  >
                    <font-awesome-icon :icon="['fas', 'diagram-project']" class="text-primary-500/70 text-xs shrink-0" />
                    <span class="text-sm text-gray-800 truncate flex-1" :class="{ 'font-semibold': activeId === wf.id }">{{ wf.name }}</span>
                    <!-- health dot -->
                    <span
                      v-if="wf.last_run_status"
                      :class="healthDotClass(wf.last_run_status)"
                      :title="wf.last_run_error || wf.last_run_status"
                    ></span>
                  </div>
                  <!-- overflow -->
                  <DropdownMenu class="shrink-0">
                    <template #trigger>
                      <span class="opacity-0 group-hover:opacity-100 transition-opacity" title="More actions">
                        <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" />
                      </span>
                    </template>
                    <DropdownMenuItem @click="selectWorkflow(wf)">Edit</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'draft'" @click="submitForReview(wf)">Submit for review</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'pending_review'" @click="askApprove(wf)">Approve</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'pending_review'" @click="askReject(wf)">Reject</DropdownMenuItem>
                    <DropdownMenuItem @click="confirmDelete(wf)">
                      <span class="text-red-dark">Delete</span>
                    </DropdownMenuItem>
                  </DropdownMenu>
                </div>
              </div>
            </template>

            <div v-if="!isLoading && !filteredWorkflows.length" class="px-4 py-10 text-center">
              <p class="text-sm text-gray-500">{{ searchQuery ? 'No matches.' : 'No workflows yet.' }}</p>
              <button v-if="!searchQuery" class="btn-accent-sm mt-3" @click="startNew">
                <font-awesome-icon :icon="['fas', 'plus']" /> New Workflow
              </button>
            </div>
          </div>
        </div>
      </aside>

      <!-- ── Right: composer bar + canvas ─────────────────────────────────── -->
      <section class="flex-1 min-w-0 flex flex-col">
        <!-- Composer bar: name + cadence + lifecycle actions for the workflow
             currently loaded on the canvas. -->
        <div class="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-gray-200 bg-white">
          <div class="flex-1 min-w-[180px]">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Name</label>
            <input
              v-model.trim="composer.name"
              type="text"
              placeholder="e.g. Readmission cohort"
              class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
            />
          </div>
          <div class="w-40">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Cadence</label>
            <select v-model="composer.expected_cadence" class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>

          <!-- Run order: workflows materialize low→high, so a workflow that reads
               another's output table must have a higher order than it. -->
          <div class="w-28">
            <label class="block text-[11px] font-medium text-gray-500 mb-1" title="Workflows run low to high. Set higher than any workflow whose output this one reads.">
              Run order
            </label>
            <input
              v-model.number="composer.post_process_order"
              type="number"
              min="0"
              step="1"
              class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
            />
          </div>

          <!-- status chip for the loaded workflow -->
          <div v-if="activeWorkflow" class="pb-1.5">
            <span :class="statusBadgeClass(activeWorkflow.status)">
              <span :class="statusDotClass(activeWorkflow.status)"></span>{{ statusLabel(activeWorkflow.status) }}
            </span>
          </div>

          <div class="flex items-center gap-2 ml-auto pb-0.5">
            <button
              v-if="canvasAgentAllowed"
              class="btn-secondary-sm"
              :class="{ 'bg-gray-100': agentOpen }"
              :aria-pressed="agentOpen"
              :title="agentOpen ? 'Close the canvas agent' : 'Ask the agent to help compose this workflow'"
              @click="toggleAgent"
            >
              <font-awesome-icon :icon="agentIcon" />
              Agent
            </button>
            <button v-if="activeId" class="btn-secondary-sm" @click="startNew">Clear</button>
            <button class="btn-primary-sm" :disabled="!canSave || saving" @click="saveWorkflow">
              <font-awesome-icon v-if="saving" :icon="['fas', 'arrows-rotate']" spin />
              {{ activeId ? 'Save changes' : 'Save' }}
            </button>
            <!-- Lifecycle actions match the loaded workflow's status -->
            <button
              v-if="activeWorkflow && activeWorkflow.status === 'draft'"
              class="btn-secondary-sm" :disabled="actingId === activeId"
              @click="submitForReview(activeWorkflow)"
            >Submit</button>
            <button
              v-if="activeWorkflow && activeWorkflow.status === 'pending_review'"
              class="btn-accent-sm" :disabled="actingId === activeId"
              @click="askApprove(activeWorkflow)"
            >Approve</button>
          </div>
        </div>
        <p v-if="composerError" class="px-4 pt-2 text-xs text-red-dark">{{ composerError }}</p>

        <!-- The canvas fills the rest -->
        <div class="flex-1 min-h-0 p-4">
          <WorkflowJoinCanvas
            ref="canvas"
            v-model="studioConfig"
            :workflow-meta="canvasWorkflowMeta"
            class="wf-canvas-fill"
          />
        </div>
      </section>

      <CanvasAgentPanel
        v-if="canvasAgentAllowed && agentOpen"
        :container-width="agentContainerWidth"
        @close="toggleAgent"
        @workflow-saved="refreshWorkflows"
      />
    </div>

    <!-- Confirm dialog (delete + approve/reject) -->
    <transition name="wf-fade">
    <div
      v-if="confirm.visible"
      class="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50"
      @click="closeConfirm"
    >
      <div class="wf-fade-panel bg-white rounded-lg p-6 max-w-sm w-full shadow-xl" @click.stop>
        <h3 class="text-lg font-medium text-gray-900">{{ confirm.title }}</h3>
        <p class="mt-2 text-sm text-gray-500">{{ confirm.body }}</p>
        <div class="mt-5 flex justify-end gap-2">
          <button class="btn-secondary" @click="closeConfirm">Cancel</button>
          <button :class="confirm.confirmClass" :disabled="confirm.busy" @click="confirm.onConfirm">
            {{ confirm.busy ? 'Working…' : confirm.confirmLabel }}
          </button>
        </div>
      </div>
    </div>
    </transition>

    <!-- Toasts (slide-in, auto-dismiss, icon) -->
    <transition name="wf-toast">
      <div v-if="errorMessage" class="fixed bottom-4 right-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md z-50">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="text-red-dark mt-0.5" />
        <p class="text-sm text-red-800">{{ errorMessage }}</p>
      </div>
    </transition>
    <transition name="wf-toast">
      <div v-if="successMessage" class="fixed bottom-4 right-4 flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-md z-50">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-dark mt-0.5" />
        <p class="text-sm text-green-800">{{ successMessage }}</p>
      </div>
    </transition>

    <!-- Add Data Connection: connect an external warehouse (Snowflake / Azure / JDBC-ODBC) -->
    <AddDataConnectionModal
      :visible="connectionModalVisible"
      @close="connectionModalVisible = false"
      @created="onConnectionCreated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { z } from 'zod';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import WorkflowJoinCanvas from './WorkflowJoinCanvas.vue';
import CanvasAgentPanel from './CanvasAgentPanel.vue';
import AddDataConnectionModal from './AddDataConnectionModal.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import DropdownMenu from '@/components/widgetcomponents/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/widgetcomponents/ui/DropdownMenuItem.vue';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { BrowserStore } from '@/utils/BrowserStore';

const API_BASE = '/api/etl_module/data-workflows/';
const agentStore = new BrowserStore('canvas-agent-open-v1', z.object({ open: z.boolean().default(false) }));

// Status groups, in the order they appear in the rail.
const STATUS_ORDER = ['draft', 'pending_review', 'approved'];

export default {
  name: 'OverviewETLWorkflowBuilder',

  components: {
    WorkflowJoinCanvas,
    CanvasAgentPanel,
    AddDataConnectionModal,
    ComponentHeader,
    DropdownMenu,
    DropdownMenuItem,
  },

  data() {
    return {
      workflows: [],
      searchQuery: '',
      isLoading: false,
      errorMessage: null,
      successMessage: null,

      railCollapsed: false,
      connectionModalVisible: false,

      agentIcon: faWandMagicSparkles,
      agentOpen: agentStore.load().open,
      splitRowWidth: 0,
      splitRowObserver: null,

      // Composer: the workflow currently loaded on the canvas. activeId is null
      // for a fresh (unsaved) workflow.
      activeId: null,
      composer: { name: '', expected_cadence: 'weekly', post_process_order: 0 },
      studioConfig: { steps: [], primary_key_columns: [] },
      composerError: null,
      saving: false,

      // Id of the workflow a lifecycle action is currently running on.
      actingId: null,

      // Shared confirm dialog (delete / approve / reject).
      confirm: {
        visible: false, title: '', body: '', confirmLabel: '',
        confirmClass: 'btn-danger', busy: false, onConfirm: () => {},
      },
    };
  },

  computed: {
    filteredWorkflows() {
      let filtered = this.workflows;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(w => w.name.toLowerCase().includes(query));
      }
      return filtered;
    },

    // Rail list bucketed by status (Draft / Pending Review / Approved).
    groupedWorkflows() {
      const byStatus = {};
      for (const s of STATUS_ORDER) byStatus[s] = [];
      for (const wf of this.filteredWorkflows) {
        (byStatus[wf.status] || (byStatus[wf.status] = [])).push(wf);
      }
      for (const s of Object.keys(byStatus)) {
        byStatus[s].sort((a, b) => a.name.localeCompare(b.name));
      }
      return STATUS_ORDER.map(status => ({ status, items: byStatus[status] || [] }));
    },

    activeWorkflow() {
      return this.activeId ? this.workflows.find(w => w.id === this.activeId) || null : null;
    },

    canSave() {
      return !!this.composer.name && !!(this.studioConfig.steps && this.studioConfig.steps.length);
    },

    // Identifies the workflow under edit for the agent's context, so it can
    // say "the Readmission cohort workflow" rather than "the canvas". Null
    // for a brand-new composition that has no row yet.
    canvasAgentAllowed() {
      return canAccessFeature(FEATURES.MODULE_CANVAS_AGENT);
    },

    agentContainerWidth() {
      return Math.max(0, this.splitRowWidth - (this.railCollapsed ? 48 : 288));
    },

    canvasWorkflowMeta() {
      if (!this.activeId) return null;
      const wf = (this.workflows || []).find(w => w.id === this.activeId);
      return wf ? { id: wf.id, name: wf.name, status: wf.status } : null;
    },
  },

  mounted() {
    this.refreshWorkflows();
    this.splitRowObserver = new ResizeObserver(([row]) => { this.splitRowWidth = row.contentRect.width; });
    this.splitRowObserver.observe(this.$refs.splitRow);
  },

  beforeUnmount() {
    if (this.splitRowObserver) this.splitRowObserver.disconnect();
  },

  methods: {
    toggleAgent() {
      this.agentOpen = !this.agentOpen;
      agentStore.save({ open: this.agentOpen });
    },

    // --- rail helpers ---
    statusLabel(status) {
      return {
        draft: 'Draft',
        pending_review: 'Pending Review',
        approved: 'Approved'
      }[status] || status;
    },

    statusBadgeClass(status) {
      const base = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ';
      const variant = {
        draft: 'bg-gray-100 text-gray-700 border-gray-300',
        pending_review: 'bg-amber-100 text-amber-800 border-amber-300',
        approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      }[status] || 'bg-gray-100 text-gray-700 border-gray-300';
      return base + variant;
    },

    statusDotClass(status) {
      const base = 'h-1.5 w-1.5 rounded-full ';
      return base + ({
        draft: 'bg-gray-500',
        pending_review: 'bg-amber-500',
        approved: 'bg-emerald-600',
      }[status] || 'bg-gray-500');
    },

    healthDotClass(runStatus) {
      const base = 'inline-block h-2 w-2 rounded-full shrink-0 ';
      if (runStatus === 'success') return base + 'bg-green-dark animate-pulse-soft';
      if (runStatus === 'failed') return base + 'bg-red-dark';
      return base + 'bg-gray-300';
    },

    flashSuccess(message) {
      this.successMessage = message;
      setTimeout(() => { this.successMessage = null; }, 3000);
    },

    onConnectionCreated(conn) {
      this.flashSuccess(`Data connection “${conn.name}” added.`);
    },

    flashError(message) {
      this.errorMessage = message;
      setTimeout(() => { this.errorMessage = null; }, 6000);
    },

    async refreshWorkflows() {
      this.isLoading = true;
      this.errorMessage = null;
      try {
        const { data } = await axios.get(API_BASE);
        // Tolerate a paginated response: the rail reads workflows.length and
        // iterates groupedWorkflows, both of which break on a bare object.
        this.workflows = Array.isArray(data) ? data : (data.results || []);
      } catch (error) {
        this.flashError(error.response?.data?.error || 'Failed to load workflows');
        console.error('Error loading workflows:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // --- composer: load / new / save ---
    startNew() {
      this.activeId = null;
      this.composer = { name: '', expected_cadence: 'weekly', post_process_order: 0 };
      this.studioConfig = { steps: [], primary_key_columns: [] };
      this.composerError = null;
    },

    // Load a saved workflow onto the canvas. The canvas rehydrates its node
    // graph from studio_config (watches modelValue).
    selectWorkflow(wf) {
      this.activeId = wf.id;
      this.composer = {
        name: wf.name || '',
        expected_cadence: wf.expected_cadence || 'weekly',
        post_process_order: wf.post_process_order ?? 0,
      };
      this.studioConfig = wf.studio_config
        ? JSON.parse(JSON.stringify(wf.studio_config))
        : { steps: [], primary_key_columns: [] };
      this.composerError = null;
    },

    async saveWorkflow() {
      if (!this.studioConfig.steps || !this.studioConfig.steps.length) {
        this.composerError = 'Add at least one data source on the canvas.';
        return;
      }
      this.composerError = null;
      this.saving = true;
      const payload = {
        name: this.composer.name,
        expected_cadence: this.composer.expected_cadence,
        post_process_order: this.composer.post_process_order || 0,
        studio_config: this.studioConfig,
      };
      try {
        if (this.activeId) {
          const { data } = await axios.patch(`${API_BASE}${this.activeId}/`, payload);
          this.flashSuccess('Workflow updated.');
          await this.refreshWorkflows();
          if (data && data.id) this.activeId = data.id;
        } else {
          const { data } = await axios.post(API_BASE, payload);
          this.flashSuccess('Workflow created.');
          await this.refreshWorkflows();
          // Keep the new workflow loaded so the user can immediately Submit.
          if (data && data.id) this.selectWorkflow(this.workflows.find(w => w.id === data.id) || data);
        }
      } catch (error) {
        const data = error.response?.data;
        this.flashError(
          data?.error ||
          (data && typeof data === 'object' ? Object.values(data).flat()[0] : null) ||
          'Failed to save workflow'
        );
      } finally {
        this.saving = false;
      }
    },

    // --- lifecycle actions ---
    // Submit is non-destructive → fire directly.
    submitForReview(wf) {
      return this._action(wf, 'submit-for-review', 'Submitted for review.');
    },

    askApprove(wf) {
      this._openConfirm({
        title: 'Approve workflow?',
        body: `Approving “${wf.name}” freezes its SQL and schedules it to materialize on its cadence.`,
        confirmLabel: 'Approve',
        confirmClass: 'btn-accent',
        onConfirm: () => this._action(wf, 'approve', 'Workflow approved.'),
      });
    },
    askReject(wf) {
      this._openConfirm({
        title: 'Reject workflow?',
        body: `“${wf.name}” will be sent back to draft for edits.`,
        confirmLabel: 'Reject',
        confirmClass: 'btn-secondary',
        onConfirm: () => this._action(wf, 'reject', 'Workflow sent back to draft.'),
      });
    },
    confirmDelete(wf) {
      this._openConfirm({
        title: 'Delete workflow?',
        body: `This permanently deletes “${wf.name}”. This cannot be undone.`,
        confirmLabel: 'Delete',
        confirmClass: 'btn-danger',
        onConfirm: () => this._deleteWorkflow(wf),
      });
    },

    _openConfirm({ title, body, confirmLabel, confirmClass, onConfirm }) {
      this.confirm = { visible: true, busy: false, title, body, confirmLabel, confirmClass, onConfirm };
    },
    closeConfirm() {
      this.confirm.visible = false;
    },

    async _action(wf, verb, successText) {
      this.confirm.busy = true;
      this.actingId = wf.id;
      try {
        await axios.post(`${API_BASE}${wf.id}/${verb}/`);
        this.closeConfirm();
        this.flashSuccess(successText);
        await this.refreshWorkflows();
        // Keep the composer in sync if the acted-on workflow is loaded.
        if (this.activeId === wf.id) {
          const updated = this.workflows.find(w => w.id === wf.id);
          if (updated) this.selectWorkflow(updated);
        }
      } catch (error) {
        this.closeConfirm();
        this.flashError(error.response?.data?.error || `Failed to ${verb.replace(/-/g, ' ')}`);
      } finally {
        this.confirm.busy = false;
        this.actingId = null;
      }
    },

    async _deleteWorkflow(wf) {
      this.confirm.busy = true;
      this.actingId = wf.id;
      try {
        await axios.delete(`${API_BASE}${wf.id}/`);
        this.closeConfirm();
        this.flashSuccess('Workflow deleted.');
        // If the deleted workflow was loaded, clear the composer.
        if (this.activeId === wf.id) this.startNew();
        await this.refreshWorkflows();
      } catch (error) {
        this.closeConfirm();
        this.flashError(error.response?.data?.error || 'Failed to delete workflow');
      } finally {
        this.confirm.busy = false;
        this.actingId = null;
      }
    },
  }
};
</script>

<style scoped>
/* Elevated motion layer — additive polish, no design-system tokens changed. */

/* Skeleton shimmer for the loading state. */
.skeleton-shimmer {
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* Softer, slower pulse than Tailwind's default for the success health dot. */
.animate-pulse-soft {
  animation: pulse-soft 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse-soft {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(56, 154, 108, 0.5); }
  50% { opacity: 0.85; box-shadow: 0 0 0 4px rgba(56, 154, 108, 0); }
}

/* The canvas fills the composer pane (overrides its fixed 440px height). */
.wf-canvas-fill { height: 100%; }
.wf-canvas-fill :deep(.wf-canvas-wrap) { height: 100%; }

/* Toast: slide up + fade. */
.wf-toast-enter-active, .wf-toast-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.wf-toast-enter-from, .wf-toast-leave-to { opacity: 0; transform: translateY(12px); }

/* Confirm dialog: backdrop fade + panel scale-in. */
.wf-fade-enter-active, .wf-fade-leave-active { transition: opacity 0.16s ease; }
.wf-fade-enter-from, .wf-fade-leave-to { opacity: 0; }
.wf-fade-enter-active .wf-fade-panel { transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1); }
.wf-fade-enter-from .wf-fade-panel { transform: translateY(6px) scale(0.97); }

/* Respect reduced-motion preferences. */
@media (prefers-reduced-motion: reduce) {
  .skeleton-shimmer, .animate-pulse-soft,
  .wf-toast-enter-active, .wf-toast-leave-active,
  .wf-fade-enter-active, .wf-fade-leave-active,
  .wf-fade-enter-active .wf-fade-panel { animation: none; transition: none; }
}
</style>
